/**
 * Какие экшены попадают в запись прохождения обучения и в каком виде.
 *
 * Запись должна проигрываться так же, как запись сетевого матча: экшены
 * диспатчатся по очереди в стор, и поле приходит в то же состояние. Сетевая
 * запись — это все, что применилось к ОБЩЕМУ состоянию партии на сервере
 * (у сервера нет клиентских middleware). Здесь то же правило, только на
 * клиенте: пишется экшен, изменивший state.shared. Так в запись попадают и
 * игровые действия, и служебные LOAD_STATE/SET_POLICY_MARKET/START_GAME с
 * noServerSync (фильтр по noServerSync их потерял бы — см. тот же разбор в
 * performStartGame на сервере), а модалки, тосты и прочий UI, который
 * клиентские middleware при просмотре порождают заново сами, — нет.
 *
 * Сверх общего состояния:
 *  - экшены движка сценария (TUTORIAL_*): по ним при просмотре восстанавливается
 *    прогресс главы — шаг, открытая модалка, завершение;
 *  - то, на что игрок смотрел (выбор отряда, карточки фракций/политик, правила),
 *    пишется не как есть, а меткой TUTORIAL_REPLAY_UI_EVENT: редьюсеров у нее
 *    нет, просмотр она не меняет (иначе поверх записи у зрителя открывались бы
 *    правила и выделялись отряды), а при разборе видно, что игрок изучал;
 *  - действия, которые обучение не пропустило (гейт tutorialMiddleware), —
 *    меткой TUTORIAL_REPLAY_ACTION_BLOCKED: где игрок пытался сделать «не то»;
 *  - уход из главы — меткой TUTORIAL_REPLAY_RUN_LEFT.
 */

export const TUTORIAL_REPLAY_MARKER_TYPES = Object.freeze({
  UI_EVENT: 'TUTORIAL_REPLAY_UI_EVENT',
  ACTION_BLOCKED: 'TUTORIAL_REPLAY_ACTION_BLOCKED',
  RUN_LEFT: 'TUTORIAL_REPLAY_RUN_LEFT',
});

// Экшены движка сценария, из которых при просмотре собирается прогресс главы.
// TUTORIAL_COMPLETION_SAVED не пишется: это ответ сервера конкретному игроку
export const TUTORIAL_ENGINE_ACTION_TYPES = new Set([
  'TUTORIAL_START_CHAPTER',
  'TUTORIAL_ADVANCE_STEP',
  'TUTORIAL_SET_MODAL',
  'TUTORIAL_CLOSE_MODAL',
  'TUTORIAL_CHAPTER_COMPLETED',
]);

// Действия интерфейса, по которым продвигаются шаги «изучи»: выбор отряда и
// ячейки, карточки фракций и политик, боковые панели, правила
export const TUTORIAL_UI_EVENT_ACTION_TYPES = new Set([
  'SELECT_UNIT',
  'SET_INSPECTED_HEX',
  'FACTION_CARD_OVERLAY_TOGGLED',
  'POLICY_CARD_OVERLAY_TOGGLED',
  'SIDE_MENU_BROUGHT_TO_FRONT',
  'SET_RULES_ACTIVE_SECTION',
  'RULES_SECTION_SCROLLED',
  'SHOW_CONSTRUCTION_PHASE_INFO_MODAL',
  'HIDE_CONSTRUCTION_PHASE_INFO_MODAL',
  // Флаг этой модалки живет в ОБЩЕМ состоянии, но это чистый интерфейс: в
  // сетевую запись он не попадает (noServerSync), а открытая при просмотре
  // модалка улучшения дала бы зрителю улучшить отряд прямо в записи
  'SHOW_UNIT_UPGRADE_MODAL',
  'HIDE_UNIT_UPGRADE_MODAL',
]);

// Не пишутся никогда — как и в записи сетевого матча (NON_RECORDED_ACTION_TYPES
// в performStartGame). RESET_STATE — выход из главы, запись на нем
// заканчивается
export const TUTORIAL_NON_RECORDED_ACTION_TYPES = new Set([
  'SET_SELECTED_TOKEN',
  'SET_BOT_CONTROLLER',
  'RESET_STATE',
]);

const pickTypeAndPayload = (action) => (
  action.payload === undefined ? {type: action.type} : {type: action.type, payload: action.payload}
);

/**
 * Запись одного примененного экшена или null, если он в запись не идет.
 *
 * @param {Object} action - экшен, прошедший гейт обучения
 * @param {Object} options
 * @param {boolean} options.isSharedStateChanged - экшен изменил state.shared
 * @param {number} options.recordedAt - момент применения (мс), для разбора пауз
 * @param {Object|null} [options.tutorialMeta] - шаг главы после применения (для экшенов движка)
 * @returns {Object|null}
 */
export const buildTutorialReplayRecord = (action, {isSharedStateChanged, recordedAt, tutorialMeta = null}) => {
  if (!action || typeof action.type !== 'string' || TUTORIAL_NON_RECORDED_ACTION_TYPES.has(action.type)) {
    return null;
  }

  if (TUTORIAL_UI_EVENT_ACTION_TYPES.has(action.type)) {
    return {
      type: TUTORIAL_REPLAY_MARKER_TYPES.UI_EVENT,
      payload: {action: pickTypeAndPayload(action)},
      noServerSync: true,
      recordedAt,
    };
  }

  if (TUTORIAL_ENGINE_ACTION_TYPES.has(action.type)) {
    // Шаг сценария на момент записи: конфиги глав меняются, и через месяц
    // stepIndex из старой записи указывал бы уже на другой шаг
    return tutorialMeta
      ? {...action, tutorialMeta, recordedAt}
      : {...action, recordedAt};
  }

  if (isSharedStateChanged) {
    // Экшен целиком, со всеми флагами (noServerSync, __tutorialBot) — как в
    // сетевой записи
    return {...action, recordedAt};
  }

  return null;
};

/**
 * Шаг главы для записи: номер, id, всего шагов и открытая модалка — из
 * конфига главы на момент прохождения
 */
export const buildTutorialReplayMeta = (tutorialState, chapter) => {
  if (!tutorialState?.chapterId || !chapter) {
    return null;
  }
  const {stepIndex} = tutorialState;
  return {
    chapterId: tutorialState.chapterId,
    stepIndex,
    stepsCount: chapter.steps.length,
    stepId: chapter.steps[stepIndex]?.id ?? null,
    activeModalId: tutorialState.activeModalId ?? null,
    isChapterCompleted: Boolean(tutorialState.isChapterCompleted),
  };
};

/**
 * Метка действия, которое гейт обучения не пропустил.
 */
export const buildTutorialBlockedActionRecord = (action, {stepIndex, stepId, recordedAt}) => ({
  type: TUTORIAL_REPLAY_MARKER_TYPES.ACTION_BLOCKED,
  payload: {
    actionType: action?.type ?? null,
    stepIndex: Number.isInteger(stepIndex) ? stepIndex : null,
    stepId: stepId ?? null,
  },
  noServerSync: true,
  recordedAt,
});

export const buildTutorialRunLeftRecord = ({stepIndex, stepId, recordedAt}) => ({
  type: TUTORIAL_REPLAY_MARKER_TYPES.RUN_LEFT,
  payload: {
    stepIndex: Number.isInteger(stepIndex) ? stepIndex : null,
    stepId: stepId ?? null,
  },
  noServerSync: true,
  recordedAt,
});
